import { defineComponent as _defineComponent } from 'vue'
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "stat-card-value-row" }
const _hoisted_2 = { class: "stat-card-label" }
const _hoisted_3 = {
  key: 0,
  class: "stat-card-footer"
}

import { ref, onMounted, computed, watch } from 'vue'
import type { Component } from 'vue'
import { TrendingUp, TrendingDown } from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'StatCard',
  props: {
    variant: { default: 'primary' },
    icon: {},
    label: { default: '' },
    value: { default: '' },
    footer: { default: '' },
    badge: { default: '' },
    badgeVariant: { default: 'up' },
    animateValue: { type: Boolean, default: true }
  },
  setup(__props: any) {

const TrendingUpIcon = TrendingUp
const TrendingDownIcon = TrendingDown

const props = __props

// --------------- Animated counter ---------------
const cardRef = ref<HTMLElement>()
const valueRef = ref<HTMLElement>()
const displayValue = ref('0')
const hasAnimated = ref(false)

const numericValue = computed(() => {
  if (typeof props.value === 'number') return props.value
  const parsed = parseFloat(String(props.value).replace(/[^0-9.-]/g, ''))
  return isNaN(parsed) ? null : parsed
})

const isNumeric = computed(() => numericValue.value !== null)

const animatedValue = computed(() => {
  if (!props.animateValue || !isNumeric.value) return props.value
  return displayValue.value
})

function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return num.toLocaleString('en-US')
  return num.toString()
}

function animateCount(target: number, duration = 800) {
  if (hasAnimated.value) {
    displayValue.value = formatNumber(target)
    return
  }
  hasAnimated.value = true

  const start = 0
  const startTime = performance.now()

  function step(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const eased = 1 - Math.pow(1 - progress, 3)
    const current = Math.round(start + (target - start) * eased)
    displayValue.value = formatNumber(current)

    if (progress < 1) {
      requestAnimationFrame(step)
    }
  }

  requestAnimationFrame(step)
}

watch(() => props.value, () => {
  if (numericValue.value !== null && props.animateValue) {
    hasAnimated.value = false
    animateCount(numericValue.value)
  }
})

onMounted(() => {
  if (!props.animateValue || !isNumeric.value) return
  if (!cardRef.value) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && numericValue.value !== null) {
          animateCount(numericValue.value)
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.3 }
  )

  observer.observe(cardRef.value)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["stat-card", `stat-card--${_ctx.variant}`]),
    ref_key: "cardRef",
    ref: cardRef
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["stat-card-icon", `stat-card-icon--${_ctx.variant}`])
    }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.icon), { class: "h-5 w-5" }))
    ], 2),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("p", {
        class: "stat-card-value",
        ref_key: "valueRef",
        ref: valueRef
      }, [
        _renderSlot(_ctx.$slots, "value", {}, () => [
          _createTextVNode(_toDisplayString(animatedValue.value), 1)
        ])
      ], 512),
      (_ctx.badge)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: _normalizeClass(["stat-card-badge", `stat-card-badge--${_ctx.badgeVariant}`])
          }, [
            (_ctx.badgeVariant === 'up')
              ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(TrendingUpIcon)), {
                  key: 0,
                  class: "h-3 w-3"
                }))
              : (_ctx.badgeVariant === 'down')
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(TrendingDownIcon)), {
                    key: 1,
                    class: "h-3 w-3"
                  }))
                : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(_ctx.badge), 1)
          ], 2))
        : _createCommentVNode("", true),
      _renderSlot(_ctx.$slots, "badge")
    ]),
    _createElementVNode("p", _hoisted_2, [
      _renderSlot(_ctx.$slots, "label", {}, () => [
        _createTextVNode(_toDisplayString(_ctx.label), 1)
      ])
    ]),
    (_ctx.footer || _ctx.$slots.footer)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, [
          _renderSlot(_ctx.$slots, "footer", {}, () => [
            _createTextVNode(_toDisplayString(_ctx.footer), 1)
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})